import spacetime from "spacetime";
import { formatPrice, getCompactPrice, decodeHtmlEntity, priceFitsWithCurrency, getCellMaxLength, parseCurrencyPosition } from "../Utils/utils";

export default class HotelDatePickerService {
    constructor(callbacks = {}) {
        this.cache = {};
        this.fetchedKeys = new Set();
        this.loadingKeys = new Set();
        this._processedCache = null;
        this._processedCacheKey = null;
        this._endpoint = '/api/hotel-availability';

        this.onUpdate = callbacks.onUpdate || (() => {});
        this.onLoadingStart = callbacks.onLoadingStart || (() => {});
        this.currencyEntityForNotice = null;
    }

    createKey(hotelId, monthStr) {
        return `${hotelId}-${monthStr}`;
    }

    async fetchAvailability(hotelId, monthsToProcess) {
        if (!hotelId || hotelId == 0) return;

        const allRequestedKeys = monthsToProcess.map(m => this.createKey(hotelId, m));
        const keysToFetch = allRequestedKeys.filter(key =>
            !this.fetchedKeys.has(key) && !this.loadingKeys.has(key)
        );

        if (keysToFetch.length === 0) {
            this.onUpdate(this.getProcessedAvailability(hotelId));
            return;
        }

        keysToFetch.forEach(key => this.loadingKeys.add(key));
        this.onLoadingStart(Array.from(this.loadingKeys));

        try {
            await Promise.all(keysToFetch.map(async (key) => {
                const date = key.split('-').slice(1).join('-');

                try {
                    const response = await fetch(`${location.origin}${this._endpoint}?hotel=${hotelId}&date=${date}`);
                    if (response.ok) {
                        const data = await response.json();
                        this.cache[key] = data.calendar || {};
                    }
                    else {
                        this.cache[key] = {};
                    }
                } catch (err) {
                    this.cache[key] = {};
                } finally {
                    this.fetchedKeys.add(key);
                    this.loadingKeys.delete(key);
                    this._processedCache = null;
                }
            }));

        } catch (e) {
        } finally {
            this.onUpdate(this.getProcessedAvailability(hotelId));
        }
    }

    getProcessedAvailability(hotelId) {
        const cacheKey = `${hotelId}:` + Array.from(this.fetchedKeys).sort().join(',');
        if (this._processedCacheKey === cacheKey && this._processedCache !== null) {
            return this._processedCache;
        }

        const hotelKeys = Array.from(this.fetchedKeys).filter(k => k.startsWith(`${hotelId}-`));
        const today = spacetime().format('iso').substring(0, 10);
        let calendarData = {};

        const maxLength = getCellMaxLength();

        hotelKeys.forEach(key => {
            const monthData = this.cache[key] || {};
            Object.entries(monthData).forEach(([date, dayInfo]) => {
                if (date < today) {
                   return;
                }

                const info = JSON.parse(JSON.stringify(dayInfo));
                calendarData[date] = info;
            });
        });

        const availableDays = Object.values(calendarData).filter(d => d.isAvailable && d.price);
        const sampleDay = availableDays.find(d => d.currencyCode && d.displayPrice) ?? null;

        let showCurrency = null;
        if (sampleDay) {
            const locale = document.documentElement.lang || window.currentLocale || 'en';
            const { hasSpace } = parseCurrencyPosition(sampleDay.displayPrice);
            const decodedCurrency = decodeHtmlEntity(sampleDay.currencyCode);
            const worstPriceLen = Math.max(...availableDays.map(d => {
                const raw = Math.round(parseFloat(d.price));
                const compact = getCompactPrice(raw);
                const isCompacted = /[KMم]/.test(compact);
                if (isCompacted) return compact.length;
                const numericPart = decodeHtmlEntity(d.displayPrice || '').replace(/[^\d.,]/g, '');
                return numericPart.length || new Intl.NumberFormat(locale).format(raw).length;
            }));
            showCurrency = priceFitsWithCurrency(worstPriceLen, decodedCurrency, hasSpace, maxLength);
            this.currencyEntityForNotice = showCurrency ? null : decodedCurrency;
        } else {
            this.currencyEntityForNotice = null;
        }

        Object.values(calendarData).forEach(info => {
            info.rawPrice = parseFloat(info.price) || 0;
            info.price = formatPrice(info, maxLength, showCurrency);
        });

        this._processedCacheKey = cacheKey;
        this._processedCache = calendarData;

        return calendarData;
    }

    isRangeAvailable(hotelId, arrival, departure, minNightsConfig) {
        if (!hotelId || !arrival) return true;

        const calendarData = this.getProcessedAvailability(hotelId) || {};

        if (Object.keys(calendarData).length === 0) {
            return true;
        }

        const arrivalDayInfo = calendarData[arrival];

        if (arrivalDayInfo) {
            if (arrivalDayInfo.isAvailable === false || arrivalDayInfo.restrictions?.isClosedForArrival === true || arrivalDayInfo.restrictions?.isClosedOut === true) {
                return false;
            }
        }

        if (!departure) return true;

        const start = spacetime(arrival);
        const end = spacetime(departure);
        const selectedNights = start.diff(end, 'day');

        if (selectedNights <= 0) return false;

        if (arrivalDayInfo) {
            const minNightsRequired = parseInt(arrivalDayInfo.restrictions?.restrictionUnitAmount || minNightsConfig);
            if (!isNaN(minNightsRequired) && selectedNights < minNightsRequired) {
                return false;
            }
        }

        return this._checkIntervalConsistency(arrival, selectedNights, calendarData);
    }

    _checkIntervalConsistency(arrival, nights, calendarData) {
        let arrivalST = spacetime(arrival);

        for (let night = 0; night <= nights; night++) {
            const currentDay = arrivalST.add(night, 'day').format('iso').substring(0, 10);
            const dayInfo = calendarData[currentDay];

            if (night === 0) {
                if (dayInfo && (dayInfo.isAvailable === false || dayInfo.restrictions?.isClosedForArrival === true))
                    return false;
            }
            else if (night === nights) {
                if (dayInfo && dayInfo.restrictions?.isClosedForDeparture === true) {
                    return false;
                }
            }
            else {
                if (dayInfo && (dayInfo.isAvailable === false || dayInfo.restrictions?.isClosedOut === true)) {
                    return false;
                }
            }
        }
        return true;
    }

}
